import { useEffect, useState } from "react";
import { Bell, X, Share } from "lucide-react";
import { toast } from "sonner";
import {
  enablePush,
  needsHomeScreen,
  permissionState,
  promptDismissed,
  dismissPrompt,
  pushSupported,
  currentSubscription,
} from "@/lib/push";

/** One-time soft prompt to turn on phone alerts. Shown on the dashboard. */
export const PushPrompt = () => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported() || promptDismissed()) return;
    if (permissionState() !== "default" && !needsHomeScreen()) return;
    currentSubscription().then((sub) => {
      if (!sub) setShow(true);
    });
  }, []);

  const close = () => {
    dismissPrompt();
    setShow(false);
  };

  const turnOn = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Phone alerts are on");
      setShow(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  const homeScreen = needsHomeScreen();

  return (
    <div
      className="surface-card p-5 mb-8 flex items-start gap-4"
      style={{ borderColor: "var(--lime)", background: "var(--lime-glow)" }}
      data-testid="push-prompt"
    >
      <div
        className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: "var(--surface)", color: "var(--lime)" }}
      >
        <Bell size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm mb-1">Get alerts on your phone</div>
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          {homeScreen
            ? "Tap the Share icon in Safari, choose \"Add to Home Screen\", then open the app from your home screen to turn alerts on."
            : "Know the moment a call gets booked, a deal moves, or your coach replies — even when the app is closed."}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {homeScreen ? (
            <span className="font-mono text-[11px] flex items-center gap-1.5" style={{ color: "var(--lime)" }}>
              <Share size={12} /> Share → Add to Home Screen
            </span>
          ) : (
            <button
              onClick={turnOn}
              disabled={busy}
              className="btn-primary text-sm flex items-center gap-2"
              data-testid="push-prompt-enable-button"
            >
              {busy ? <span className="spinner" /> : <Bell size={14} />}
              {busy ? "Turning on..." : "Turn on alerts"}
            </button>
          )}
          <button onClick={close} className="btn-ghost text-sm" data-testid="push-prompt-dismiss-button">
            Not now
          </button>
        </div>
      </div>
      <button onClick={close} className="p-1 transition hover:opacity-70" data-testid="push-prompt-close-button">
        <X size={16} style={{ color: "var(--text-muted)" }} />
      </button>
    </div>
  );
};

export default PushPrompt;
